import { ANIMATION_DURATION } from 'app/components/content-block/constants';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import React, { useCallback, useEffect } from 'react';

interface UseMobileHeaderProps {
  backArrowMobileHeaderRef: React.MutableRefObject<HTMLDivElement> | null;
  containerRef: React.MutableRefObject<HTMLDivElement> | null;
  scrollableContainerRef: React.MutableRefObject<HTMLElement> | null;
}

export const useMobileHeader = ({
  backArrowMobileHeaderRef,
  containerRef,
  scrollableContainerRef,
}: UseMobileHeaderProps): void => {
  const client = useMiniappSdk();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        client?.header?.hideHeader();
      }
    };

    client?.header?.hideHeader();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [client]);

  const handleSetStyles = useCallback(() => {
    if (!backArrowMobileHeaderRef || !containerRef || !scrollableContainerRef) return;

    const headerElement = backArrowMobileHeaderRef?.current;
    const headerPositions = headerElement?.getBoundingClientRect();
    const headerBottomPosition = headerPositions?.bottom;

    const scrollableContainer = scrollableContainerRef?.current?.getBoundingClientRect();
    const scrollableContainerTopPosition = scrollableContainer?.top;

    if (
      containerRef?.current
      && headerElement
      && headerBottomPosition / 2 >= scrollableContainerTopPosition
    ) {
      headerElement.style.backgroundColor = 'white';
      headerElement.style.boxShadow = '0px 1px #696E7233';
    } else if (containerRef?.current && backArrowMobileHeaderRef?.current) {
      headerElement.style.backgroundColor = 'transparent';
      headerElement.style.boxShadow = 'none';
    }
  }, [backArrowMobileHeaderRef, containerRef, scrollableContainerRef]);

  const handleTouchEnd = useCallback(() => {
    setTimeout(handleSetStyles, ANIMATION_DURATION / 3);
  }, [handleSetStyles]);

  useEffect(() => {
    const currentContainerRef = containerRef?.current;

    if (currentContainerRef) {
      currentContainerRef.addEventListener('scroll', handleSetStyles);
      currentContainerRef.addEventListener('touchmove', handleSetStyles);
      currentContainerRef.addEventListener('touchend', handleTouchEnd);
    }

    handleSetStyles();

    return () => {
      if (currentContainerRef) {
        currentContainerRef.removeEventListener('scroll', handleSetStyles);
        currentContainerRef.removeEventListener('touchmove', handleSetStyles);
        currentContainerRef.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [containerRef, handleSetStyles, handleTouchEnd]);
};
